import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import TransactionsTable from './components/TransactionsTable';
import TransactionFormModal from './components/TransactionFormModal';
import SplitTransactionModal from './components/SplitTransactionModal';
import TransferModal from './components/TransferModal';

import { transactionService } from '../../services/transactionService';
import { realtimeService } from '../../services/realtimeService';


import { convertToBarbadosTime, formatDateInBarbados, getISODateInBarbados } from '../../utils/timezone';

import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import UpgradeModal from '../../components/ui/UpgradeModal';

const TransactionsManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { canUse } = useSubscription();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    account: '',
    category: '',
    type: '',
    amountMin: '',
    amountMax: '',
    searchTerm: ''
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitTransaction, setSplitTransaction] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState({ feature: '', limit: 0, current: 0 });
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Transactions Management', path: '/transactions-management' }
  ];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadTransactions();
    }
  }, [user]);

  // Real-time subscription for transactions
  useEffect(() => {
    if (!user) return;

    const transactionChannel = realtimeService?.subscribeToTransactions(
      (newTransaction) => {
        // On INSERT: Add new transaction to the list
        loadTransactions();
      },
      (updatedTransaction) => {
        // On UPDATE: Update transaction in the list
        loadTransactions();
      },
      (deletedTransaction) => {
        // On DELETE: Remove transaction from the list
        loadTransactions();
      }
    );

    return () => {
      realtimeService?.unsubscribe(transactionChannel);
    };
  }, [user]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await transactionService?.getAll();
      setTransactions(data);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    return transactions?.filter(t => {
      if (filters?.dateFrom && convertToBarbadosTime(t?.date) < convertToBarbadosTime(filters?.dateFrom)) return false;
      if (filters?.dateTo && convertToBarbadosTime(t?.date) > convertToBarbadosTime(filters?.dateTo)) return false;
      if (filters?.account && t?.account !== filters?.account) return false;
      if (filters?.category && t?.category !== filters?.category) return false;
      if (filters?.type && t?.type !== filters?.type) return false;
      if (filters?.amountMin && Math.abs(t?.amount) < parseFloat(filters?.amountMin)) return false;
      if (filters?.amountMax && Math.abs(t?.amount) > parseFloat(filters?.amountMax)) return false;
      if (filters?.searchTerm) {
        const search = filters?.searchTerm?.toLowerCase();
        return t?.description?.toLowerCase()?.includes(search) ||
               t?.category?.toLowerCase()?.includes(search) ||
               t?.account?.toLowerCase()?.includes(search);
      }
      return true;
    });
  }, [transactions, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      account: '',
      category: '',
      type: '',
      amountMin: '',
      amountMax: '',
      searchTerm: ''
    });
  };

  const handleAddTransaction = async (transactionData) => {
    try {
      await transactionService?.create(transactionData);
      await loadTransactions();
      setShowAddModal(false);
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err?.message || 'Failed to create transaction');
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setShowAddModal(true);
  };

  const handleUpdateTransaction = async (transactionData) => {
    try {
      await transactionService?.update(editingTransaction?.id, transactionData);
      await loadTransactions();
      setShowAddModal(false);
      setEditingTransaction(null);
    } catch (err) {
      console.error('Error updating transaction:', err);
      setError(err?.message || 'Failed to update transaction');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await transactionService?.delete(transactionId);
        await loadTransactions();
      } catch (err) {
        console.error('Error deleting transaction:', err);
        setError(err?.message || 'Failed to delete transaction');
      }
    }
  };

  const handleSplitTransaction = (transaction) => {
    setSplitTransaction(transaction);
    setShowSplitModal(true);
  };

  const handleSplitSubmit = async (splitData) => {
    try {
      await transactionService?.delete(splitTransaction?.id);
      
      for (const split of splitData?.splits) {
        await transactionService?.create({
          accountId: splitTransaction?.accountId,
          categoryId: split?.categoryId,
          date: splitTransaction?.date,
          description: `${splitTransaction?.description} - ${split?.category}`,
          amount: splitTransaction?.type === 'expense' ? -Math.abs(split?.amount) : Math.abs(split?.amount),
          type: splitTransaction?.type,
          cleared: splitTransaction?.cleared
        });
      }
      
      await loadTransactions();
      setShowSplitModal(false);
      setSplitTransaction(null);
    } catch (err) {
      console.error('Error splitting transaction:', err);
      setError(err?.message || 'Failed to split transaction');
    }
  };

  const handleTransferSubmit = async (transferData) => {
    try {
      // Create outgoing transaction (from account)
      await transactionService?.create({
        accountId: transferData?.fromAccountId,
        categoryId: transferData?.outgoingCategoryId,
        date: transferData?.date,
        description: transferData?.description || `Transfer to ${transferData?.toAccount}`,
        amount: transferData?.outgoingType === 'expense' ? -Math.abs(transferData?.amount) : Math.abs(transferData?.amount),
        type: transferData?.outgoingType,
        cleared: false,
        notes: transferData?.notes || null
      });

      // Create incoming transaction (to account)
      await transactionService?.create({
        accountId: transferData?.toAccountId,
        categoryId: transferData?.incomingCategoryId,
        date: transferData?.date,
        description: transferData?.description || `Transfer from ${transferData?.fromAccount}`,
        amount: transferData?.incomingType === 'income' ? Math.abs(transferData?.amount) : -Math.abs(transferData?.amount),
        type: transferData?.incomingType,
        cleared: false,
        notes: transferData?.notes || null
      });

      await loadTransactions();
      setShowTransferModal(false);
    } catch (err) {
      console.error('Error creating transfer:', err);
      setError(err?.message || 'Failed to create transfer');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTransactions?.length === 0) return;
    if (window.confirm(`Delete ${selectedTransactions?.length} selected transactions?`)) {
      try {
        await transactionService?.bulkDelete(selectedTransactions);
        await loadTransactions();
        setSelectedTransactions([]);
      } catch (err) {
        console.error('Error bulk deleting transactions:', err);
        setError(err?.message || 'Failed to delete transactions');
      }
    }
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Description', 'Account', 'Category', 'Type', 'Amount'];
    const rows = filteredTransactions?.map(t => [
      formatDateInBarbados(t?.date),
      t?.description,
      t?.account,
      t?.category,
      t?.type,
      t?.amount
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${getISODateInBarbados()}.csv`;
    a?.click();
  };

  const handleTransferClick = async () => {
    const { allowed } = await canUse('transfers');
    
    if (!allowed) {
      setUpgradeInfo({ feature: 'transfers', limit: 0, current: 0 });
      setShowUpgradeModal(true);
      return;
    }

    setShowTransferModal(true);
  };

  const handleSplitClick = async (transaction) => {
    const { allowed } = await canUse('split');
    
    if (!allowed) {
      setUpgradeInfo({ feature: 'split', limit: 0, current: 0 });
      setShowUpgradeModal(true);
      return;
    }

    setSelectedTransactions(transaction);
    setShowSplitModal(true);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-16"></div>
          <p className="text-muted-foreground font-caption">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Transactions Management | Rovyn Budget</title>
        <meta name="description" content="Manage all your financial transactions with advanced filtering, editing, and split transaction capabilities" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 py-24 md:py-32 max-w-[1400px]">
            <Breadcrumbs items={breadcrumbItems} />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-16 mb-24">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-card-foreground mb-8">
                  Transactions Management
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Comprehensive transaction control with filtering, editing, and advanced features
                </p>
              </div>

              <div className="flex flex-wrap gap-12">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportCSV}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ArrowLeftRight"
                  iconPosition="left"
                  onClick={handleTransferClick}
                >
                  Transfer
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Transaction
                </Button>
              </div>
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-16 mb-24">
                <p className="text-sm text-error font-caption">{error}</p>
              </div>
            )}

            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />

            <div className="mt-24">
              <TransactionsTable
                transactions={filteredTransactions}
                selectedTransactions={selectedTransactions}
                onSelectionChange={setSelectedTransactions}
                onEdit={handleEditTransaction}
                onDelete={handleDeleteTransaction}
                onSplit={handleSplitTransaction}
                onBulkDelete={handleBulkDelete}
                onBulkCategoryChange={() => setShowBulkCategoryModal(true)}
              />
            </div>
          </div>
        </main>
      </div>
      {showAddModal && (
        <TransactionFormModal
          transaction={editingTransaction}
          onSubmit={editingTransaction ? handleUpdateTransaction : handleAddTransaction}
          onClose={() => {
            setShowAddModal(false);
            setEditingTransaction(null);
          }}
        />
      )}
      {showSplitModal && (
        <SplitTransactionModal
          transaction={splitTransaction}
          onSubmit={handleSplitSubmit}
          onClose={() => {
            setShowSplitModal(false);
            setSplitTransaction(null);
          }}
        />
      )}
      {showTransferModal && (
        <TransferModal
          onSubmit={handleTransferSubmit}
          onClose={() => setShowTransferModal(false)}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature={upgradeInfo?.feature}
          limit={upgradeInfo?.limit}
          current={upgradeInfo?.current}
        />
      )}
    </>
  );
};

export default TransactionsManagement;